import { useRoute } from "wouter";
import { Trophy, Radio, Timer } from "lucide-react";
import { useGameByShareCode } from "@/lib/queries";
import { useGameStream } from "@/lib/useGameStream";
import { useElapsed, formatDuration } from "@/lib/duration";
import { cn } from "@/lib/cn";

// Public spectator. No auth. Renders the box score, frame-by-frame ticker,
// and lights up "LIVE" when the game is in progress.
export default function Spectator() {
  const [, params] = useRoute("/g/:shareCode");
  const shareCode = params?.shareCode ?? null;
  const { data: game, isLoading } = useGameByShareCode(shareCode);
  useGameStream(shareCode);
  const elapsed = useElapsed(
    game?.startedAt ?? null,
    game?.completedAt ?? null,
    game?.durationSeconds ?? null,
  );

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  if (!game) {
    return (
      <div className="p-8 text-center space-y-2">
        <h1 className="text-2xl font-display uppercase">Not found</h1>
        <p className="text-muted-foreground text-sm">
          This game link doesn't exist or has been removed.
        </p>
      </div>
    );
  }

  const isLive = game.status === "live";
  const team1Label = game.team1Players.map((p) => p.name).join(" & ") || "Team 1";
  const team2Label = game.team2Players.map((p) => p.name).join(" & ") || "Team 2";

  return (
    <div className="min-h-dvh bg-background">
      <header
        className="flex items-center justify-between h-12 px-4 border-b border-border"
        style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
      >
        <div className="font-display uppercase tracking-tight text-sm">
          Cornholio · /g/{game.shareCode}
        </div>
        {isLive && (
          <div className="inline-flex items-center gap-1.5 text-xs font-display uppercase tracking-widest text-primary">
            <Radio size={12} className="animate-pulse" />
            Live
          </div>
        )}
        {game.status === "complete" && (
          <div className="inline-flex items-center gap-1.5 text-xs font-display uppercase tracking-widest text-muted-foreground">
            <Trophy size={12} /> Final
          </div>
        )}
      </header>

      <div className="grid grid-cols-2 gap-px bg-border">
        <SideTile
          label={team1Label}
          score={game.team1Score}
          won={game.winningTeam === 1}
          accent="primary"
        />
        <SideTile
          label={team2Label}
          score={game.team2Score}
          won={game.winningTeam === 2}
          accent="secondary"
        />
      </div>

      <div className="px-4 py-3 text-center text-xs font-display uppercase tracking-widest text-muted-foreground flex items-center justify-center gap-3">
        <span>
          Round {game.currentRound} · to {game.targetScore}
        </span>
        {(elapsed.live || game.status === "complete") && (
          <span
            className={cn(
              "inline-flex items-center gap-1 tabular-nums",
              elapsed.live ? "text-primary" : "text-muted-foreground",
            )}
          >
            <Timer size={11} />
            {formatDuration(elapsed.seconds)}
          </span>
        )}
      </div>

      {game.frames && game.frames.length > 0 && (
        <ol className="divide-y divide-border border-t border-border">
          {game.frames.slice().reverse().map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-3 px-4 py-3"
            >
              <span className="w-10 text-sm font-display tabular-nums text-muted-foreground">
                R{f.roundNumber}
              </span>
              <span className="flex-1 text-sm">
                {f.scoringTeam === null
                  ? "Tied round"
                  : `${f.scoringTeam === 1 ? team1Label : team2Label} +${f.netPoints}`}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SideTile({
  label,
  score,
  won,
  accent,
}: {
  label: string;
  score: number;
  won: boolean;
  accent: "primary" | "secondary";
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 py-8 bg-card",
        won && "ring-4 ring-inset ring-primary",
      )}
    >
      <span className="text-xs font-display uppercase tracking-widest text-muted-foreground px-4 text-center">
        {label}
      </span>
      <span
        className={cn(
          "font-display tabular-nums leading-none",
          "text-[clamp(4rem,20vw,9rem)]",
          accent === "primary" ? "text-primary" : "text-foreground",
        )}
      >
        {score}
      </span>
    </div>
  );
}
